import React, { FC, ReactNode, RefObject, useCallback, useMemo } from 'react';
import StateManager from 'react-select';
import type { ProjectId } from 'api/models';
import { FieldOptionBase } from 'api/tasks';
import { SingleUserField } from 'components/organisms/tasks/fields';
import { ClassNameOnlyProps } from 'components/ozone-component';
// eslint-disable-next-line import/no-internal-modules
import { useUserFieldOptions } from 'hooks/use-user-field-options';
import { Popover, Select } from '../components/components';

type Props = ClassNameOnlyProps & {
  field: SingleUserField;
  title: ReactNode;
  onChange: (latest: number | null) => void;
  projectId: ProjectId;
  isPrivate: boolean;
  selectRef: RefObject<StateManager>;
};

export const ControlledSingleUserFieldEditorPopover: FC<Props> = ({
  field,
  projectId,
  isPrivate,
  title,
  onChange,
  selectRef,
  className,
}) => {
  const options = useUserFieldOptions({
    projectId,
    isPrivate,
  });

  const onValueChange = useCallback(
    (latest: FieldOptionBase) => onChange(latest?.value ?? null),
    [onChange],
  );

  const usersInOptions = useMemo(
    () => options.flatMap((option) => option.options),
    [options],
  );

  const selectedUser = useMemo(
    () =>
      usersInOptions.find((u) => {
        return u.value === field.value;
      }),
    [field.value, usersInOptions],
  );

  return (
    <Popover className={className}>
      <Popover.Title>{title}</Popover.Title>
      <Select
        ref={selectRef}
        type="user"
        options={options}
        value={selectedUser}
        onChange={onValueChange}
      />
    </Popover>
  );
};
